import { z } from 'zod';

/**
 * Budget policy actions when limits are exceeded
 */
export type BudgetPolicy = 'halt' | 'truncate' | 'degrade_model' | 'alert';

/**
 * Budget configuration schema for Zod validation
 */
export const BudgetConfigSchema = z.object({
  /** Maximum spend per session in USD */
  usd: z.number().positive().optional(),
  
  /** Maximum spend per request in cents */
  perRequestCents: z.number().positive().optional(),
  
  /** Action to take when budget is exceeded */
  onExceed: z.enum(['halt', 'truncate', 'degrade_model', 'alert']).optional(),
});

/**
 * Budget configuration for FinOps guardrails
 */
export type BudgetConfig = z.infer<typeof BudgetConfigSchema>;

/**
 * Budget tracking state for a session
 */
export interface BudgetState {
  /** Total spend in USD */
  totalUsd: number;
  
  /** Number of requests made */
  requestCount: number;
  
  /** Cost of last request in cents */
  lastRequestCents: number;
  
  /** Whether budget has been exceeded */
  exceeded: boolean;
  
  /** Remaining budget in USD */
  remainingUsd: number;
}

/**
 * Cost alert payload
 */
export interface CostAlertPayload {
  sessionId?: string | undefined;
  totalUsd: number;
  limitUsd: number;
  requestCents: number;
  requestLimitCents: number;
  policy: BudgetPolicy;
  model?: string | undefined;
  timestamp: number;
}

/**
 * Cost update payload
 */
export interface CostUpdatePayload {
  sessionId?: string | undefined;
  totalUsd: number;
  requestCents: number;
  requestCount: number;
  exceeded: boolean;
  model?: string | undefined;
  timestamp: number;
}

/**
 * Token usage breakdown
 */
export interface TokenUsage {
  /** Input/prompt tokens */
  input: number;
  
  /** Output/completion tokens */
  output: number;
  
  /** Total tokens (input + output) */
  total: number;
}

/**
 * Cost calculation result
 */
export interface CostCalculation {
  /** Cost in USD */
  costUsd: number;
  
  /** Cost in cents */
  costCents: number;
  
  /** Tokens used */
  tokens: number;
  
  /** Model name (if provided) */
  model?: string | undefined;
}

/**
 * FinOps event types
 */
export type FinOpsEventType = 'cost:alert' | 'cost:update' | 'budget:reset' | 'session:start' | 'session:end';

/**
 * FinOps event
 */
export interface FinOpsEvent {
  type: FinOpsEventType;
  timestamp: number;
  payload: CostAlertPayload | CostUpdatePayload | Record<string, unknown>;
}

/**
 * FinOps context interface for integrating with adapters
 * 
 * Adapters should implement cost tracking by calling these methods
 * after each API request.
 * 
 * @example
 * ```typescript
 * const context: FinOpsContext = createFinOpsContext({
 *   budgetManager: createBudgetManager({ usd: 10 }),
 * });
 * 
 * // After an API call
 * await context.trackCost({
 *   sessionId: 'session-123',
 *   tokens: 1000,
 *   costPerMTokenUsd: 0.002,
 *   model: 'gpt-4o-mini',
 * });
 * ```
 */
export interface FinOpsContext {
  /**
   * Track cost for a request
   * 
   * @param options - Cost tracking options
   * @param options.sessionId - Session identifier (optional)
   * @param options.tokens - Number of tokens used
   * @param options.costPerMTokenUsd - Cost per million tokens in USD
   * @param options.model - Model name (optional)
   * @returns Updated budget state
   */
  trackCost(options: {
    sessionId?: string | undefined;
    tokens: number;
    costPerMTokenUsd: number;
    model?: string | undefined;
  }): Promise<BudgetState>;

  /**
   * Check if a request would exceed the budget
   * 
   * @param options - Budget check options
   * @param options.sessionId - Session identifier (optional)
   * @param options.estimatedTokens - Estimated token count
   * @param options.costPerMTokenUsd - Cost per million tokens in USD
   * @returns True if the request would exceed budget
   */
  wouldExceed(options: {
    sessionId?: string | undefined;
    estimatedTokens: number;
    costPerMTokenUsd: number;
  }): Promise<boolean>;

  /**
   * Get budget state for a session
   * 
   * @param sessionId - Session identifier (optional, uses default if not provided)
   * @returns Current budget state
   */
  getState(sessionId?: string | undefined): Promise<BudgetState>;

  /**
   * Reset budget for a session
   * 
   * @param sessionId - Session identifier (optional, resets default if not provided)
   */
  reset(sessionId?: string | undefined): Promise<void>;

  /**
   * Register a cost alert handler
   * 
   * @param handler - Callback function for cost alerts
   */
  onCostAlert(handler: (payload: CostAlertPayload) => void | Promise<void>): void;

  /**
   * Register a cost update handler
   * 
   * @param handler - Callback function for cost updates
   */
  onCostUpdate(handler: (payload: CostUpdatePayload) => void | Promise<void>): void;

  /**
   * Get all active sessions
   * 
   * @returns Array of session IDs
   */
  getSessions(): Promise<string[]>;
}

/**
 * FinOps context configuration
 */
export interface FinOpsContextConfig {
  /** Budget manager instance (from @thenoo/finops) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  budgetManager?: any;
  
  /** Default budget config (used if budgetManager not provided) */
  budgetConfig?: BudgetConfig | undefined;
  
  /** Enable debug logging */
  debug?: boolean | undefined;
}

